import { useEffect, useMemo, MutableRefObject } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { UserRole } from '@myapp/contract'

import { FieldWrapper, FormSection } from '../../../shared/ui'
import { FormInput } from '../../../shared/ui/controls/FormInput'
import { creatableRoles, getPositionFromRole } from '../../../entities/user'
import { userFormSchema, userFormSchemaWithPassword, UserFormValues } from '../model/userFormSchema'
import { TechnicianFields } from './TechnicianFields'

interface Props {
  mode: 'create' | 'edit'
  actorRoles: UserRole[]
  defaultValues?: Partial<UserFormValues>
  onSubmit: (data: UserFormValues) => Promise<void>
  isLoading?: boolean
  submitRef?: MutableRefObject<(() => void) | null>
}

const DEFAULT_TECHNICIAN_PROFILE = {
  workingDays: [1, 2, 3, 4, 5],
  shiftStart: '07:00',
  shiftEnd: '16:00',
  breakStart: '12:00',
  trades: [],
}

export function UserFormWidget({ mode, actorRoles, defaultValues, onSubmit, isLoading, submitRef }: Props) {
  const availableRoles = creatableRoles(actorRoles)
  const allRoles: UserRole[] = mode === 'edit' && defaultValues?.roles
    ? Array.from(new Set([...availableRoles, ...defaultValues.roles]))
    : availableRoles

  const initialRoles: [UserRole, ...UserRole[]] = defaultValues?.roles?.length
    ? (defaultValues.roles as [UserRole, ...UserRole[]])
    : (availableRoles.length > 0 ? [availableRoles[0]] : ['WORKER']) as [UserRole, ...UserRole[]]

  const showPassword = actorRoles.includes('SUPERUSER') && mode === 'create'

  const resolver = useMemo(
    () => zodResolver(showPassword ? userFormSchemaWithPassword : userFormSchema),
    [showPassword],
  )

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver,
    defaultValues: {
      email: '',
      name: '',
      roles: initialRoles,
      ...(initialRoles.includes('WORKER') ? { technicianProfile: DEFAULT_TECHNICIAN_PROFILE } : {}),
      ...defaultValues,
    },
  })

  useEffect(() => {
    if (mode === 'edit' && defaultValues) {
      reset({
        email: defaultValues.email ?? '',
        name: defaultValues.name ?? '',
        roles: defaultValues.roles?.length ? (defaultValues.roles as [UserRole, ...UserRole[]]) : initialRoles,
        technicianProfile: defaultValues.technicianProfile ?? (defaultValues.roles?.includes('WORKER') ? DEFAULT_TECHNICIAN_PROFILE : undefined),
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, mode, reset])

  const selectedRoles = watch('roles') ?? []

  useEffect(() => {
    if (selectedRoles.includes('WORKER') && getValues('technicianProfile.trades') === undefined) {
      setValue('technicianProfile', DEFAULT_TECHNICIAN_PROFILE, { shouldValidate: false })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoles])

  const handleFormSubmit = handleSubmit(
    async (data) => {
      try {
        await onSubmit(data)
      } catch (err: any) {
        const body = err?.response?.data
        const details: Array<{ field: string; message: string }> = body?.details ?? []
        if (details.length > 0) {
          details.forEach(({ field, message }) => {
            setError(field as any, { type: 'server', message })
          })
        } else if (body?.error === 'USER_ALREADY_EXISTS' || body?.error === 'USER_ALREADY_EXISTS_ARCHIVED') {
          setError('email', { type: 'server', message: body.message })
        } else {
          setError('root', {
            type: 'server',
            message: body?.message ?? 'Fehler beim Speichern',
          })
        }
      }
    },
    (validationErrors) => {
      console.error('[UserFormWidget] Validation failed:', validationErrors)
    },
  )

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleFormSubmit
    }
  }, [submitRef, handleFormSubmit])

  return (
    <form onSubmit={handleFormSubmit} className="flex flex-row gap-[60px]">
      <FormSection title="Benutzerdaten">
        <FieldWrapper label="Rolle" error={(errors.roles as any)?.message ?? (errors.roles as any)?.[0]?.message}>
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <div className="flex flex-wrap gap-2">
                {allRoles.map((role) => {
                  const active = field.value?.includes(role)
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => {
                        const current = field.value ?? []
                        if (active) {
                          if (current.length === 1) return
                          field.onChange(current.filter((r) => r !== role))
                        } else {
                          field.onChange([...current, role])
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                        active
                          ? 'bg-[#004494] text-white border-[#004494]'
                          : 'bg-white text-[#004494] border-[#004494] hover:bg-blue-50'
                      }`}
                    >
                      {getPositionFromRole(role)}
                    </button>
                  )
                })}
              </div>
            )}
          />
        </FieldWrapper>

        <FieldWrapper label="Name" error={errors.name?.message}>
          <FormInput
            name="name"
            control={control}
            placeholder="Vollständiger Name"
          />
        </FieldWrapper>

        <FieldWrapper label="E-Mail" error={errors.email?.message}>
          <FormInput
            name="email"
            control={control}
            type="email"
            placeholder="E-Mail Adresse"
          />
        </FieldWrapper>

        {showPassword && (
          <FieldWrapper label="Passwort" error={errors.password?.message}>
            <FormInput
              name="password"
              control={control}
              type="password"
              placeholder="Mindestens 8 Zeichen"
              autoComplete="new-password"
            />
          </FieldWrapper>
        )}

        {errors.root?.message && (
          <div className="text-[#FB2424] font-['PT_Sans'] text-[14px] italic">
            {errors.root.message}
          </div>
        )}
      </FormSection>
      {selectedRoles.includes('WORKER') && (
        <TechnicianFields
          control={control as any}
          errors={{
            shiftStart: errors.technicianProfile?.shiftStart?.message,
            shiftEnd: errors.technicianProfile?.shiftEnd?.message,
            breakStart: errors.technicianProfile?.breakStart?.message,
          }}
        />
      )}
    </form>
  )
}
